import * as Service from 'types/service';
import { FunctionComponent, useState } from 'react';
import { PartyStatusIconData } from 'components/settings/business-defined-states/partyStatusIcons';
import { PrintChitSource } from 'constants/PrinterConstants';
import { dashboard } from 'types';
import { generateStateSelectorStatus } from 'utils/partyUtils';
import { getPartyStateSelectorOptions } from 'utils/partyStateSelectorOptions';
import { handlePartyStatusSelection } from 'utils/partyStatusSelection';
import { secondaryStatesSelector } from 'selectors/service/secondaryStatesSelector';
import { useAsyncRender } from 'contexts/asyncRender';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useSelector } from 'react-redux';
import PartyStatusIcon from './PartyStatusIcon';
import PortalDropdown, { PortalDropdownOptionProps } from 'components/inputs/PortalDropdown';
import SecondaryStateItem from 'components/service/dropdown/SecondaryStateItem';
import clsx from 'clsx';

interface Props {
  party: Service.Party;
  handleUpdatePartyState?(party: Service.Party, state: string, secondaryState: string | null): void;
  printOnSeat?: boolean;
  onPrintChit?(party: Service.Party, source: PrintChitSource): void;
  onSelect?(option: Service.StateDescription): void;
  position?: 'auto' | 'bottom' | 'top';
  disabled?: boolean;
}

type OptionProps = PortalDropdownOptionProps<Service.StateDescription>;

const toPartyStatusIconData = (option: Service.StateDescription): PartyStatusIconData => ({
  className: option.className,
  strokeColorClassName: option.strokeColorClassName ?? '',
  border: option.border ?? '',
  icon: option.icon ?? '',
  label: option.stateName ?? undefined,
  primaryState: option.primaryState as dashboard.PartyState,
  key: `${option.primaryState}-${option.secondaryValue ?? 'default'}`,
});

/**
 * Dropdown on PartyStatusIcon to change the status of a party.
 */
const PartyStatusIconSelector: FunctionComponent<Props> = ({
  party,
  handleUpdatePartyState,
  printOnSeat = false,
  onPrintChit,
  onSelect,
  position = 'bottom',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const asyncRender = useAsyncRender();
  const isPartyStatusRefresh = useFeatureFlag('PARTY_STATUS_REFRESH');
  const isPrintPartiallySeatedEnabled = useFeatureFlag('PRINT_PARTIALLY_SEATED');
  const secondaryStates = useSelector(secondaryStatesSelector);
  const status = generateStateSelectorStatus(party);
  const selectorStatus = status as unknown as Parameters<
    typeof getPartyStateSelectorOptions
  >[0]['status'];
  const options = getPartyStateSelectorOptions({
    party,
    status: selectorStatus,
    secondaryStates,
    partyStatusRefreshEnabled: isPartyStatusRefresh,
  });

  const dropdownClassName = clsx(
    'PartyStateSelector-dropdown',
    'PartyStatusIconSelector-dropdown',
    {
      'PartyStateSelector-dropdown--refresh': isPartyStatusRefresh,
    }
  );

  const PartyStatusDropdownItem: FunctionComponent<OptionProps> = ({ option, isSelected }) => {
    if (isSelected === undefined) {
      return <PartyStatusIcon state={toPartyStatusIconData(option)} isSelected={isOpen} />;
    }

    const isOptionSelected =
      isSelected ||
      (option.primaryState === party.state &&
        (option.secondaryValue ?? null) === (party.secondaryState ?? null));

    return <SecondaryStateItem option={option} isSelected={isOptionSelected} />;
  };

  const handleStatusSelect = (option: Service.StateDescription): void => {
    handlePartyStatusSelection({
      party,
      option,
      printOnSeat,
      onPrintChit,
      isPrintPartiallySeatedEnabled,
      confirm: asyncRender.confirm,
      onConfirmOrImmediateSelect: () => {
        handleUpdatePartyState?.(party, option.primaryState, option.secondaryValue);
        onSelect?.(option);
      },
    });
  };

  return (
    <PortalDropdown
      contextClassName={dropdownClassName}
      fitContentWidth={isPartyStatusRefresh}
      component={PartyStatusDropdownItem}
      isOptionSelected={(option, selected) =>
        option.primaryState === selected.primaryState &&
        (option.secondaryValue ?? null) === (selected.secondaryValue ?? null)
      }
      position={position}
      disabled={disabled}
      options={options}
      selectedOption={party.stateDescription}
      onSelect={handleStatusSelect}
      onOpen={setIsOpen}
      hideChevron={true}
    />
  );
};

export default PartyStatusIconSelector;
